/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import { getPrompts } from "../../../promptsManager/services/promptServices";
import { MenuItem, Grid, TextField, FormLabel, Paper } from "@material-ui/core";
import { grey } from "@material-ui/core/colors";
/**
 * This component is used for selecting a prompt.
 * @param {*} setPrompt Callback method for setting the selected prompt.
 * @param {*} languages The languages that can be selected.
 * @param {Array[Object]} namespaces The namespaces that can be selected.
 * @param {Object} prompt The selected prompt.
 * @param {Boolean} disabled Boolean
 * @param {Function} setIsLoading function to show loading indicator
 * @param {Boolean} edited
 */
export const PromptsSelector = ({ setPrompt, disabled, setIsLoading, languages, namespaces, prompt, edited }) => {
	const [selectedLanguageAndNamespace, setSelectedLanguageAndNamespace] = React.useState({
		language: undefined,
		namespace: undefined,
	});
	const [prompts, setPrompts] = React.useState(undefined);
	const [selectedPrompt, setSelectedPrompt] = React.useState(prompt);

	React.useEffect(() => {
		if (prompt !== selectedPrompt) {
			setSelectedLanguageAndNamespace(
				prompt
					? { language: prompt.language, namespace: prompt.namespace }
					: { language: undefined, namespace: undefined },
			);
			setSelectedPrompt(prompt);
		}
	}, [prompt, languages, namespaces, selectedPrompt]);

	React.useEffect(() => {
		if (!prompt && !edited) {
			setSelectedLanguageAndNamespace({ language: undefined, namespace: undefined });
		}
	}, [prompt, edited, languages, namespaces]);

	React.useEffect(() => {
		const getNewPrompts = async () => {
			setIsLoading(true);
			let newPrompts = await getPrompts(selectedLanguageAndNamespace.language, selectedLanguageAndNamespace.namespace);
			newPrompts = newPrompts.filter(
				(pr) =>
					pr.language === selectedLanguageAndNamespace.language &&
					pr.namespace === selectedLanguageAndNamespace.namespace,
			);
			if (newPrompts) setPrompts(newPrompts);
			setIsLoading(false);
		};
		if (!disabled && selectedLanguageAndNamespace.language && selectedLanguageAndNamespace.namespace) {
			getNewPrompts();
		}
	}, [selectedLanguageAndNamespace.language, setIsLoading, selectedLanguageAndNamespace.namespace, disabled]);

	const promptObject =
		(prompts && selectedPrompt && prompts?.find((x) => x.promptId === selectedPrompt.promptId)) || {};

	let promptContentTitle = "";

	if (promptObject) {
		switch (promptObject.type) {
			case "TTS":
				promptContentTitle = "Message";
				break;
			case "SSML":
				promptContentTitle = "SSML";
				break;
			case "audio":
				promptContentTitle = "ARN";
				break;
			default:
				break;
		}
	}
	const { language: selectedLanguage, namespace: selectedNamespace } = selectedLanguageAndNamespace || {};
	const validLanguage = languages.find((language) => language === selectedLanguage);
	const validNamespace = namespaces.find((namespace) => namespace === selectedNamespace);
	const validPrompt = prompt && prompts && prompts.find((promptItem) => promptItem.promptId === prompt.promptId);
	return (
		<>
			<Grid item xs={12} md={6}>
				{languages?.length && (
					<TextField
						label={"Language"}
						select
						disabled={disabled}
						className="w-100"
						value={selectedLanguage || ""}
						onChange={(event) => {
							setSelectedLanguageAndNamespace({ ...selectedLanguageAndNamespace, language: event.target.value });
							setSelectedPrompt(undefined);
							setPrompt(undefined);
						}}
					>
						{languages.map((language) => {
							return (
								<MenuItem value={language} key={language}>
									{language}
								</MenuItem>
							);
						})}
					</TextField>
				)}
			</Grid>
			<Grid item xs={12} md={6}>
				{namespaces?.length && (
					<TextField
						label={"Namespace"}
						select
						disabled={disabled}
						className="w-100"
						value={selectedNamespace || ""}
						onChange={(event) => {
							setSelectedLanguageAndNamespace({ ...selectedLanguageAndNamespace, namespace: event.target.value });
							setSelectedPrompt(undefined);
							setPrompt(undefined);
						}}
					>
						{namespaces.map((namespace) => {
							return (
								<MenuItem value={namespace} key={namespace}>
									{namespace}
								</MenuItem>
							);
						})}
					</TextField>
				)}
			</Grid>

			<Grid item xs={12} lg={6}>
				{namespaces?.length && (
					<TextField
						label={"Prompts"}
						select
						style={{ width: "100%" }}
						disabled={disabled || !validLanguage || !validNamespace}
						value={selectedPrompt?.promptId || ""}
						onChange={(event) => {
							const newPrompt = prompts.find((prompt) => prompt.promptId === event.target.value);
							setSelectedPrompt({
								promptId: newPrompt.promptId,
								namespace: newPrompt.namespace,
								language: newPrompt.language,
							});
							setPrompt({
								promptId: newPrompt.promptId,
								namespace: newPrompt.namespace,
								language: newPrompt.language,
							});
						}}
					>
						{!validPrompt && (
							<MenuItem value={""} key={"No Prompt Selected"} disabled>
								Please Select A Prompt
							</MenuItem>
						)}
						{prompts?.map((prompt) => {
							return (
								<MenuItem value={prompt.promptId} key={prompt.promptId}>
									{prompt.promptId}
								</MenuItem>
							);
						})}
					</TextField>
				)}
			</Grid>
			<Grid item xs={12}>
				{prompts !== undefined && prompts.length === 0 && !selectedPrompt && (
					<span className="mt-3 text-muted text-danger">
						No prompts were found for the selected language and namespace.
					</span>
				)}
				{!disabled && selectedPrompt && promptObject?.type && (
					<Paper color={grey} className="p-3" variant="outlined">
						<h6 style={{ fontWeight: "bold" }}>Selected Prompt</h6>
						<div className="d-flex">
							<div className="mr-3">
								<FormLabel>Type</FormLabel>
								<div className={`text-muted`}>{promptObject.type}</div>
							</div>
							<div>
								<FormLabel>{promptContentTitle}</FormLabel>
								<div className={`text-muted`}>{promptObject.prompt}</div>
							</div>
						</div>
					</Paper>
				)}
			</Grid>
		</>
	);
};
